import { styled } from "@linaria/react"

import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { MessageText } from "@/components/Chat/MessageStyles.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { CardIterate, CardPrototype, CardShare, SvgIllustration } from "@/illustrations/Illustrations.ts"
import { smallFontSize } from "@/styles/textStyles.ts"

import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { AiChatMessageContent } from "@/lib/client/generated/TypeDefinitions.ts"

export const ProjectCreatedMessage = (props: {
  message: ChatMessageModel
  content: AiChatMessageContent.InitProject.ProjectCreated
}) => {
  return (
    <AgentMessageContainer isActive={props.message.active}>
      <Vertical gap="16px">
        <Heading2>Repository is ready!</Heading2>
        <MessageText>
          Your project has been successfully mirrored! The next step is setting up a
          preview runtime environment so you can:
        </MessageText>
        <Horizontal gap="12px" wrap="wrap">
          <PrototypeCard>
            <CardIllustration><CardPrototype className={SvgIllustration} /></CardIllustration>
            <CardText>Prototype changes in real-time</CardText>
          </PrototypeCard>

          <ShareCard>
            <CardIllustration><CardShare className={SvgIllustration} /></CardIllustration>
            <CardText>Share a preview link with teammates</CardText>
          </ShareCard>

          <IterateCard>
            <CardIllustration><CardIterate className={SvgIllustration} /></CardIllustration>
            <CardText>Debug and iterate in the browser</CardText>
          </IterateCard>
        </Horizontal>
      </Vertical>
    </AgentMessageContainer>
  )
}

const Card = styled(Vertical)`
  align-items: center;

  min-width: 160px;

  min-height: 160px;
  max-height: 160px;

  flex-grow: 1;

  position: relative;

  &::before {
    content: "";
    z-index: -1;

    position: absolute;
    inset: 0;

    border-radius: 30px;
  }
`

const PrototypeCard = styled(Card)`
  &::before {
    background: radial-gradient(107.41% 148.02% at 95.73% 76.98%, #00FF36 0%, #09F54A 4%, #28D392 18%, #40BAC6 29%, #4EAAE6 38%, #54A5F3 42%, rgb(255 0 215 / 20%) 100%);
    opacity: 0.4;
  }
`

const ShareCard = styled(Card)`
  &::before {
    background: radial-gradient(139.1% 128.41% at 99.85% 110.62%, #FFA25B 2.88%, #FFD35B 31.06%, #E4FF93 100%);
    opacity: 0.5;
  }
`

const IterateCard = styled(Card)`
  &::before {
    background: radial-gradient(131.18% 161.89% at -3.55% 72.6%, #FF29EA 0%, #F64980 26%, #F36060 37%, #F36954 42%, rgb(255 234 0 / 50%) 100%);
    opacity: 0.5;
  }
`

const CardIllustration = styled.div`
  position: relative;
  top: 12px;
`

const CardText = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  left: 12px;

  text-align: center;

  ${smallFontSize};
`